import React, { useState, useCallback, createContext, useContext } from 'react';
import { uuid } from 'uuidv4';

import MarkContainer from '../containers/MarkContainer';

export interface Mark {
  id: string;
  mark_id?: string;
  type: 'negative' | 'positive';
  background: string;
  color: string;
  title: string;
  description: string;
  from_index: number;
  to_index: number;
  offsetX?: number;
  offsetY?: number;
  isNew: boolean;
  isActive: boolean;
  isDeleted?: boolean;
}

interface IShowMark {
  id: string;
  offsetX: number;
  offsetY: number;
}

interface MarkContextState {
  marks: Mark[];
  deletedMarks: string[];
  addMark(mark: Omit<Mark, 'id' | 'isNew' | 'isActive'>): void;
  removeMark(id: string): void;
  showMark(data: IShowMark): void;
  hideMark(id: string): void;
  isActive(id: string): boolean;
  setInitialMarks(marks: Omit<Mark, 'isNew' | 'isActive'>[]): void;
}

const MarkContext = createContext<MarkContextState>({} as MarkContextState);

const MarkProvider: React.FC = ({ children }) => {
  const [marks, setMarks] = useState<Mark[]>([]);
  const [deletedMarks, setDeletedMarks] = useState<string[]>([]);

  const setInitialMarks = useCallback(
    (initialMarks: Omit<Mark, 'isNew' | 'isActive'>[]) => {
      const newInitialMarks = initialMarks.map(mark => ({
        ...mark,
        isNew: false,
        isActive: false,
      }));

      setMarks(newInitialMarks);
    },
    [],
  );

  const addMark = useCallback((mark: Omit<Mark, 'id' | 'isNew'>) => {
    const newMark = {
      ...mark,
      id: uuid(),
      isNew: true,
      isActive: false,
    };

    setMarks(oldMarks => [...oldMarks, newMark]);
  }, []);

  const removeMark = useCallback((id: string) => {
    setMarks(oldMarks => {
      const oldMarkIndex = oldMarks.findIndex(oldMark => oldMark.id === id);

      if (!oldMarks[oldMarkIndex].isNew) {
        setDeletedMarks(oldDeletedMarks => [...oldDeletedMarks, id]);
      }

      return oldMarks.filter(oldMark => oldMark.id !== id);
    });
  }, []);

  const showMark = useCallback(
    ({ id, offsetX, offsetY }: IShowMark) =>
      setMarks(oldMarks =>
        oldMarks.map(oldMark =>
          oldMark.id === id
            ? { ...oldMark, offsetX, offsetY, isActive: true }
            : { ...oldMark, isActive: false },
        ),
      ),
    [],
  );

  const hideMark = useCallback(
    (id: string) =>
      setMarks(oldMarks =>
        oldMarks.map(oldMark =>
          oldMark.id === id ? { ...oldMark, isActive: false } : oldMark,
        ),
      ),
    [],
  );

  const isActive = useCallback(
    (id: string) => {
      const mark = marks.find(findMark => findMark.id === id);

      if (!mark) {
        return false;
      }

      return mark.isActive;
    },
    [marks],
  );

  const activeMarks = useCallback(() => {
    return marks.filter(mark => mark.isActive === true);
  }, [marks]);

  return (
    <MarkContext.Provider
      value={{
        marks,
        deletedMarks,
        addMark,
        removeMark,
        showMark,
        hideMark,
        isActive,
        setInitialMarks,
      }}
    >
      <MarkContainer marks={activeMarks()}>{children}</MarkContainer>
    </MarkContext.Provider>
  );
};

function useMark(): MarkContextState {
  const context = useContext(MarkContext);

  if (!context) {
    throw new Error('useMark must be user within an MarkProvider');
  }

  return context;
}

export { MarkProvider, useMark };
